import React, { useRef, type ReactNode } from "react";
import { useSelector } from "react-redux";
import { useFormik } from "formik";
import { makeStyles } from "@material-ui/core/styles";
import DialogActions from "@material-ui/core/DialogActions";
import DialogContent from "@material-ui/core/DialogContent";
import DialogTitle from "@material-ui/core/DialogTitle";
import Dialog from "src/components/Dialog";
import Box from "@material-ui/core/Box";
import TextField from "@material-ui/core/TextField";
import InputAdornment from "@material-ui/core/InputAdornment";
import ZonedKeyboardDatePicker from "src/components/ZonedKeyboardDatePicker";

import NumberField from "src/components/NumberField";
import LoadableButton from "src/components/LoadableButton";
import { updateReconciliation } from "src/apiService/modules/reconciliations";
import ConfirmDialog from "src/components/ConfirmDialog";
import { effectiveUserTimeZoneSelector } from "src/store/system/selector";
import type { Reconciliation } from "src/interfaces/reconciliation.interface";
import {
  calendarYmdInTimeZone,
  startOfCalendarDayContainingInstant,
  tryYmdStringToEndOfDayUtc,
} from "src/utils/timezone";
import type { BankAccountWithReconciliations } from "./types";

const useStyles = makeStyles({
  content: {
    "&:first-child": {
      paddingTop: 0,
    },
  },
});

interface ContentProps {
  bankAccount: BankAccountWithReconciliations;
  onClose: (dialog?: { type: "manual-reconciliation"; index: number }) => void;
  reconciliationIndex: number;
  onReconciliationUpdated: (reconciliation: Reconciliation) => void;
}

function Content({
  bankAccount,
  onClose,
  reconciliationIndex,
  onReconciliationUpdated,
}: ContentProps) {
  const timeZone = useSelector(effectiveUserTimeZoneSelector);
  const account = bankAccount.name;
  const reconciliation = bankAccount.reconciliations[reconciliationIndex];
  const isLastReconciliation =
    reconciliationIndex === bankAccount.reconciliations.length - 1;
  const reconciliationDate =
    tryYmdStringToEndOfDayUtc(reconciliation.date, timeZone) ?? null;
  const classes = useStyles();
  const applyReconciliationUpdate = async (date: Date, balance: number) => {
    const nextDateYmd = calendarYmdInTimeZone(date, timeZone);
    const result = await updateReconciliation(reconciliation.id, {
      balance,
      date: nextDateYmd,
    });
    onReconciliationUpdated({
      ...reconciliation,
      id: result.id,
      date: nextDateYmd,
      open: Boolean(result.open),
      balance: result.balance,
      bank_account_id: result.bank_account_id,
      transactions: result.transactions,
    });
    if (Array.isArray(result.affectedReconciliations)) {
      for (const affected of result.affectedReconciliations) {
        const existing = bankAccount.reconciliations.find(
          (r) => r.id === affected.id
        );
        if (!existing) continue;
        onReconciliationUpdated({
          ...existing,
          open: affected.open,
          balance: affected.balance,
        });
      }
    }
    return result?.open;
  };
  const formik = useFormik({
    initialValues: {
      confirm: false,
      date: reconciliationDate,
      balance: reconciliation.balance,
    } as { date: Date | null; balance: number | null; confirm: boolean },
    validate: (values) => {
      const errors: Record<string, string> = {};
      if (!values.balance && values.balance !== 0)
        errors.balance = "Required field";
      if (!values.date) errors.date = "Required field";
      return errors;
    },
    onSubmit: async (values) => {
      if (
        values.balance === null ||
        values.balance === undefined ||
        !values.date ||
        !reconciliation ||
        !reconciliationDate
      )
        return;
      const originalDateYmd = calendarYmdInTimeZone(
        reconciliationDate,
        timeZone
      );
      const nextDateYmd = calendarYmdInTimeZone(values.date, timeZone);
      const unchanged =
        values.balance === reconciliation.balance &&
        originalDateYmd === nextDateYmd;
      if (unchanged) {
        onClose(
          reconciliation.open
            ? { type: "manual-reconciliation", index: reconciliationIndex }
            : undefined
        );
        return;
      }
      if (isLastReconciliation) {
        const openAfterUpdate = await applyReconciliationUpdate(
          values.date,
          values.balance
        );
        onClose(
          openAfterUpdate
            ? { type: "manual-reconciliation", index: reconciliationIndex }
            : undefined
        );
        return;
      }
      formik.setValues((v) => ({ ...v, confirm: true }));
    },
    validateOnBlur: false,
    validateOnChange: false,
  });

  return (
    <>
      <form onSubmit={formik.handleSubmit}>
        <DialogContent className={classes.content}>
          <TextField
            value={account}
            inputProps={{ readOnly: true }}
            margin="dense"
            variant="outlined"
            fullWidth
            label="Account"
          />
          <NumberField
            allowNegative
            autoComplete="off"
            margin="dense"
            fullWidth
            required
            label="Ending Balance"
            value={formik.values.balance ?? ""}
            variant="outlined"
            onChange={formik.handleChange}
            name="balance"
            helperText={formik.errors?.balance || undefined}
            error={!!formik.errors?.balance}
            disabled={formik.isSubmitting}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">$</InputAdornment>
              ),
            }}
          />
          <Box pt={1}>
            <ZonedKeyboardDatePicker
              fullWidth
              variant="inline"
              inputVariant="outlined"
              disableToolbar
              format="MM/dd/yyyy"
              label="Ending Date"
              required
              autoOk
              onChange={(d) => {
                if (d) {
                  const date = startOfCalendarDayContainingInstant(d, timeZone);
                  formik.setValues((v) => ({
                    ...v,
                    date,
                  }));
                } else {
                  formik.setValues((v) => ({
                    ...v,
                    date: null,
                  }));
                }
              }}
              value={formik.values.date || null}
              helperText={formik.errors?.date || undefined}
              error={!!formik.errors?.date}
              disabled={
                bankAccount.reconciliations.length - reconciliationIndex > 1 ||
                formik.isSubmitting
              }
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <LoadableButton
            color="primary"
            variant="contained"
            type="submit"
            loading={formik.isSubmitting}
          >
            Update
          </LoadableButton>
        </DialogActions>
      </form>
      <ConfirmDialog
        open={formik.values.confirm || false}
        title="Are you sure?"
        text={`You are about to change reconciliation information for the ${account} account. Doing this will impact future reconciliations for ${account}. Are you sure you'd like to make this update?`}
        onConfirm={async () => {
          if (
            formik.values.balance === null ||
            formik.values.balance === undefined ||
            !formik.values.date ||
            !reconciliation
          )
            return;
          const openAfterUpdate = await applyReconciliationUpdate(
            formik.values.date,
            formik.values.balance
          );
          onClose(
            openAfterUpdate
              ? { type: "manual-reconciliation", index: reconciliationIndex }
              : undefined
          );
        }}
        onCancel={() => {
          formik.setValues((v) => ({ ...v, confirm: false }));
        }}
      />
    </>
  );
}

function EditReconciliationDialog({
  onClose,
  bankAccount,
  reconciliationIndex,
  onReconciliationUpdated,
}: Omit<ContentProps, "reconciliationIndex"> &
  Partial<Pick<ContentProps, "reconciliationIndex">>) {
  const ref = useRef<ReactNode | null>(null);

  if (reconciliationIndex !== undefined) {
    ref.current = (
      <Content
        key={reconciliationIndex}
        bankAccount={bankAccount}
        onClose={onClose}
        reconciliationIndex={reconciliationIndex}
        onReconciliationUpdated={onReconciliationUpdated}
      />
    );
  }
  return (
    <Dialog
      open={reconciliationIndex !== undefined}
      onClose={() => onClose()}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle>Edit Reconciliation</DialogTitle>
      {ref.current}
    </Dialog>
  );
}

export default EditReconciliationDialog;
